import React, { useEffect, useState } from "react";
import { useTranslation } from "../contexts/LocaleContext";
import { send_email } from "../apis/SendEmail";
import { get_files_list_no } from "../apis/GetFIlesListNo";
import { check_email } from "../apis/CheckEmail";
import NotificationToast from "./Toast";
import SelectOption from "./SelectOption";
import "../css/user.scss";

const SendEmailModal = ({ isOpen, onClose, templateData, onSent }) => {
  const [toEmail, setToEmail] = useState("");
  const [filesOptions, setFilesOptions] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [inputString, setInputString] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });
  const [replaceValue, setReplaceValue] = useState("");
  const [subjectReplace, setSubjectReplace] = useState("");
  const [subjectKey, setSubjectKey] = useState("");
  const [replaceKey, setReplaceKey] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setToEmail("");
      setReplaceValue("");
      setSubjectReplace("");
      setSubjectKey(templateData?.subjectlocating ?? "");
      setReplaceKey(templateData?.locating ?? "");
      setToastInfo({ message: "", type: "" });
      // default selected file to the current template/file id if available
      setSelectedFile(
        templateData?.id ??
          templateData?.fileId ??
          templateData?._id ??
          templateData?.modelId ??
          ""
      );
      setError("");

      // fetch files for the dropdown
      (async () => {
        try {
          const res = await get_files_list_no("", "");
          const list =
            res?.data?.data?.list ||
            res?.data?.data ||
            res?.data?.list ||
            res?.data ||
            [];
          setFilesOptions(Array.isArray(list) ? list : []);
        } catch (err) {
          console.error("Failed to fetch files list:", err);
          setFilesOptions([]);
        }
      })();
    }
  }, [isOpen, templateData]);

  const fileSelectOptions = [
    { value: "", label: t ? t("filter.all") : "Please select" },
    ...filesOptions.map((f, idx) => {
      const id = f?.id ?? f?.fileId ?? f?.modelId ?? idx;
      const label =
        f?.name ?? f?.title ?? f?.filename ?? f?.modelName ?? `File ${id}`;
      return { value: id, label };
    }),
  ];

  if (!isOpen) return null;

  const handleCheck = async () => {
    setToastInfo({ message: "", type: "" });
    setIsChecking(true);
    try {
      const tplId = templateData?.id ?? templateData?.templateId;
      const locating =
        replaceKey ??
        templateData?.locating ??
        templateData?.subjectlocating ??
        "";
      const replace = replaceValue ?? "";
      const subjectlocating = subjectKey ?? templateData?.subjectlocating ?? "";
      const subjectreplace = subjectReplace ?? "";
      const res = await check_email(
        tplId,
        locating,
        replace,
        subjectlocating,
        subjectreplace,
        toEmail,
        inputString
      );
      const data = res?.data;
      const msg =
        data?.message ??
        (data?.data ? JSON.stringify(data.data) : undefined) ??
        "Check completed";
      setToastInfo({ message: String(msg), type: "success" });
    } catch (err) {
      console.error("Failed to check email:", err);
      setToastInfo({ message: "Check failed", type: "error" });
    } finally {
      setIsChecking(false);
    }
  };

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError("");
    // No frontend validation required; send empty strings if fields are empty
    setIsSending(true);
    try {
      const tplId = templateData?.id ?? templateData?.templateId;
      const locating =
        replaceKey ??
        templateData?.locating ??
        templateData?.subjectlocating ??
        "";
      const replace = replaceValue ?? "";
      const subjectlocating = subjectKey ?? templateData?.subjectlocating ?? "";
      const subjectreplace = subjectReplace ?? "";
      const res = await send_email(
        tplId,
        toEmail,
        locating,
        replace,
        subjectlocating,
        subjectreplace,
        selectedFile
      );
      const code = res?.data?.code;
      if (code === 201) {
        setError("Failed to send email");
        if (onSent) onSent(false);
      } else {
        if (onSent) onSent(true);
        onClose();
      }
    } catch (err) {
      console.error("Failed to send email:", err);
      setError("Failed to send email");
      if (onSent) onSent(false);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send Email</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSend}>
          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 4h16v16H4z"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 6l-10 7L2 6"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.to_email") : "To Email"}
            </label>
            <input
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="someone@example.com"
            />
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2v6h6"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.select_file") : "Select File"}
            </label>
            <SelectOption
              value={selectedFile}
              onChange={(v) => setSelectedFile(v)}
              options={fileSelectOptions}
              ariaLabel="选择文件"
            />
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4 6h16M4 12h8"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.subject_key") : "Subject Key :"}
            </label>
            <input
              value={subjectKey}
              onChange={(e) => setSubjectKey(e.target.value)}
              placeholder="{name}"
            />
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 12h18"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3v18"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.subject_replace") : "Subject Replace"}
            </label>
            <input
              value={subjectReplace}
              onChange={(e) => setSubjectReplace(e.target.value)}
              placeholder="Subject replacement"
            />
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2v20"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 7h14"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.replace_key") : "Replace Key :"}
            </label>
            <input
              value={replaceKey}
              onChange={(e) => setReplaceKey(e.target.value)}
              placeholder="{name},{img},{link}"
            />
          </div>

          <div className="form-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="16"
                    rx="2"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 8h10"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.replace_value") : "Replace Value"}
            </label>
            <input
              value={replaceValue}
              onChange={(e) => setReplaceValue(e.target.value)}
              placeholder="value"
            />
          </div>

          <div className="form-row  preview-row">
            <label>
              <span className="label-icon" aria-hidden="true">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 12h18"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3v18"
                    stroke="#6b7280"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {t ? t("send.input_string") : "Input String"}
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                value={inputString}
                onChange={(e) => setInputString(e.target.value)}
                placeholder="account,password,key,port,smtp|host,port,account,password,SOCKS5"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn-ghost"
                onClick={handleCheck}
                disabled={isChecking}
                aria-label="Check"
                style={{ whiteSpace: "nowrap" }}
              >
                {isChecking
                  ? t
                    ? t("send.check") + "..."
                    : "Checking..."
                  : t
                  ? t("send.check")
                  : "Check"}
              </button>
            </div>
          </div>

          <div className="form-row preview-row">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setPreviewExpanded((s) => !s)}
                aria-expanded={previewExpanded}
                aria-label={previewExpanded ? "Hide preview" : "Show preview"}
                style={{ border: "none" }}
              >
                {previewExpanded ? (
                  <svg
                    t="1767761780665"
                    class="icon"
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="18654"
                    width="16"
                    height="16"
                  >
                    <path
                      d="M928.137 271c17.497 0 31.713 14.042 32 31.47v0.541a32 32 0 0 1-1.413 9.404A465.423 465.423 0 0 1 885.926 455.3l61.162 35.33c14.259 8.237 19.145 26.471 10.914 40.734-8.143 14.112-26.085 19.044-40.275 11.16l-0.44-0.25-70.871-40.937a468.499 468.499 0 0 1-118.048 87.716l52.044 90.19c8.228 14.26 3.343 32.489-10.912 40.724-14.105 8.147-32.105 3.448-40.449-10.47l-0.256-0.437-55.239-95.724c-41.668 15.337-85.97 24.876-131.831 27.749l-0.001 96.016c0 16.569-13.432 30-30 30-16.569 0-30-13.431-30-30l-0.001-96.087c-45.635-2.967-89.716-12.534-131.182-27.846l-55.336 95.892c-8.14 14.108-26.079 19.038-40.264 11.156l-0.44-0.25c-14.256-8.234-19.14-26.464-10.913-40.723l52.172-90.412a468.502 468.502 0 0 1-117.822-87.697l-71.225 41.14c-14.108 8.15-32.112 3.45-40.459-10.473l-0.256-0.437c-8.23-14.263-3.345-32.497 10.914-40.734l61.547-35.553a465.422 465.422 0 0 1-72.63-142.662 32.25 32.25 0 0 1-1.413-8.766V303c0-17.673 14.326-32 32-32 14.491 0 26.733 9.633 30.671 22.846l0.11 0.382 1.383 4.395C181.14 461.968 332.764 575.69 506.938 577.97l5.338 0.035c176.408 0 330.601-114.37 383.696-279.382l1.38-4.387 0.114-0.39c3.89-13.051 15.882-22.61 30.142-22.846h0.53z"
                      fill="#979797"
                      p-id="18655"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 12s4.5-7 9.5-7 9.5 7 9.5 7-4.5 7-9.5 7-9.5-7-9.5-7z"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            {previewExpanded && (
              <div
                className="inline-preview preview-body"
                role="region"
                aria-label="Template preview"
                style={{
                  overflow: "auto",
                }}
              >
                <div
                  className="preview-content"
                  dangerouslySetInnerHTML={{
                    __html: templateData?.content ?? "",
                  }}
                />
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={isSending}
            >
              {t ? t("send.cancel") : "Cancel"}
            </button>
            <button type="submit" className="btn-primary" disabled={isSending}>
              {isSending ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />{" "}
                  {t ? t("send.send") + "..." : "Sending..."}
                </>
              ) : t ? (
                t("send.send")
              ) : (
                "Send"
              )}
            </button>
          </div>
        </form>
      </div>
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
    </div>
  );
};

export default SendEmailModal;
